import { PersonChoiceContext, personChosenMessage, type PersonChoice } from "./person-choice";
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import {
  clampAssistantWidth,
  DEFAULT_ASSISTANT_WIDTH,
  shownAssistantWidth,
} from "@/lib/assistant-width";
import { useAgentStore, visibleUserText, type AgentMessage } from "@/stores/agent-store";
import { apolloClient } from "@/graphql/client";
import {
  agentBindingContextForPath,
  sectionForPath,
  starterPrompts,
} from "@/lib/section";
import { queriesFor } from "@/lib/agent-refresh";
import {
  createAssistantAgent,
  runAguiTurn,
  type AgentAction,
  type AgentProgress,
} from "@/lib/agent-agui";
import { executeUiTool } from "@/lib/assistant-ui-tools";
import {
  startTour,
  TOUR_ALL_ROUTES,
  type TourController,
} from "@/lib/assistant-tour-driver";
import { clearAnchorHint, showAnchorHint } from "@/lib/assistant-anchor-hint";
import { PILLARS_META, type PillarId } from "@/lib/assistant-tour-pillars";
import {
  streamPersonDetail,
  streamPersonsTable,
  type AgentEvent,
} from "@/lib/agent-stream";
import {
  deleteAgentThread,
  fetchAgentThread,
  listAgentThreads,
  postTourHintEvent,
  postTourTranscript,
  stopAgentRun,
  type AgentThreadRef,
  type TourStepPayload,
} from "@/lib/agent-sessions";
import { AgentMarkdown } from "./AgentMarkdown";
import { agentNoticeMessage } from "@/lib/agent-notice";
import { AgentCardView } from "./AgentCardView";
import { StudioNotebookChip } from "./StudioNotebookChip";
import { CompanySearchMode } from "./CompanySearchMode";
import { WebSearchToggle } from "./WebSearchToggle";
import { ChatAttachments, type ChatAttachmentsHandle } from "./ChatAttachments";
import { useStickToBottom } from "./useStickToBottom";
import { ProgressText } from "./AgentProgressText";
import { Button } from "@/components/design/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useFileDrop } from "@/components/common/use-file-drop";
import { useHasSystemPermission } from "@/auth/access-permissions";

// Right-edge overlay drawer for the Assistant (see @adr SPARK-0019-assistant-lives-on-the-right-edge@2). Slides in from
// the right; non-modal — the user can keep working in the page while the
// drawer is open. Two views: a Chats list (past threads) and an active chat
// that streams answers from the agent-service via the BFF /ai* passthroughs.
// Self-contained — does not touch chat-store. The topbar AssistantLauncher
// drives `open`; this component renders the body.

// How long person-info clicks accumulate before firing one load (≥2 → comparison table). ~1s gives
// time to move the mouse to the next person's icon in the reply; a lone click pays this delay but the
// icon's "added" highlight is instant feedback (and the profile then streams). Accumulation appends
// no message, so it never auto-scrolls the reply (and its links) out of reach mid-selection.
const PERSON_BATCH_DEBOUNCE_MS = 1000;

// How long the /tour-all section question stays open before the general tour continues.
const TOUR_QUESTION_TIMEOUT_MS = 15_000;

// Height for the context-break wrapper (section switch): the message list's viewport minus its
// py-4 paddings, so the pinned-to-bottom scroll puts the break exactly at the top edge.
const LIST_PADDING_Y = 32;
function breakMinHeight(el: HTMLElement | null): number {
  return Math.max(0, (el?.clientHeight ?? 0) - LIST_PADDING_Y);
}

// Chat previews arrive with markdown bold (e.g. "**Grow**: ..."). Render the bold spans inline so the
// single-line preview shows formatting instead of literal ** markers (full markdown would break the
// one-line truncation).
function renderInlineBold(text: string) {
  return text.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

export function AssistantDrawer() {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  const open = useAgentStore((s) => s.open);
  const setOpen = useAgentStore((s) => s.setOpen);
  const view = useAgentStore((s) => s.view);
  const showList = useAgentStore((s) => s.showList);
  const newChat = useAgentStore((s) => s.newChat);
  const openThread = useAgentStore((s) => s.openThread);
  const threads = useAgentStore((s) => s.threads);
  const threadsState = useAgentStore((s) => s.threadsState);
  const setThreads = useAgentStore((s) => s.setThreads);
  const setThreadsState = useAgentStore((s) => s.setThreadsState);
  const removeThread = useAgentStore((s) => s.removeThread);
  const messages = useAgentStore((s) => s.messages);
  const pending = useAgentStore((s) => s.pending);
  const pendingSend = useAgentStore((s) => s.pendingSend);
  // Follow-up chips a surface chose for the turn it queued (see `ask`'s `followUps`).
  const turnFollowUps = useRef<string[] | null>(null);
  const clearPendingSend = useAgentStore((s) => s.clearPendingSend);
  const agentThreadId = useAgentStore((s) => s.agentThreadId);
  const startTurn = useAgentStore((s) => s.startTurn);
  const matchSubsection = useAgentStore((s) => s.matchSubsection);
  const startInfoStream = useAgentStore((s) => s.startInfoStream);
  const appendDelta = useAgentStore((s) => s.appendDelta);
  const endTurn = useAgentStore((s) => s.endTurn);
  const signalAgentWrite = useAgentStore((s) => s.signalAgentWrite);
  const signalMatchSolve = useAgentStore((s) => s.signalMatchSolve);
  const failTurn = useAgentStore((s) => s.failTurn);
  const setAgentThreadId = useAgentStore((s) => s.setAgentThreadId);
  const setRunId = useAgentStore((s) => s.setRunId);
  const suggestions = useAgentStore((s) => s.suggestions);
  const setSuggestions = useAgentStore((s) => s.setSuggestions);
  const progress = useAgentStore((s) => s.progress);
  const addProgress = useAgentStore((s) => s.addProgress);
  const markExternalSources = useAgentStore((s) => s.markExternalSources);
  const addCard = useAgentStore((s) => s.addCard);
  const highlightAnchor = useAgentStore((s) => s.highlightAnchor);
  const setHighlightAnchor = useAgentStore((s) => s.setHighlightAnchor);
  const setHighlightHint = useAgentStore((s) => s.setHighlightHint);
  const tourActive = useAgentStore((s) => s.tourActive);
  // Off until `/company-search-mode` switches it on; the control is not even mounted meanwhile, so
  // it costs no catalogue fetch on a page nobody asked it for.
  const companySearchModeShown = useAgentStore((s) => s.companySearchModeShown);
  const tourPaused = useAgentStore((s) => s.tourPaused);
  const tourFinished = useAgentStore((s) => s.tourFinished);
  const tourStepIds = useAgentStore((s) => s.tourStepIds);
  const tourCurrentStepId = useAgentStore((s) => s.tourCurrentStepId);
  const tourStepDwellMs = useAgentStore((s) => s.tourStepDwellMs);
  const appendMessage = useAgentStore((s) => s.appendMessage);
  // Where the LOADED session's interrupted tour stopped (mirror of the session detail; set
  // locally right after a live interruption) — renders the paused-style controls in that chat.
  const resumeTour = useAgentStore((s) => s.resumeTour);
  const setResumeTour = useAgentStore((s) => s.setResumeTour);
  // Chat document drop (§G) is admin-only (system access); the drawer itself is not gated.
  const canAttachDocuments = useHasSystemPermission();
  // Couple a dropped document to the current chat: reuse the live thread id, or mint one now (mirrors the
  // tour's up-front mint at line ~561) so a drop into a not-yet-started chat still targets this session.
  const ensureThreadId = useCallback(() => {
    const cur = useAgentStore.getState().agentThreadId;
    if (cur) return cur;
    const id = crypto.randomUUID();
    setAgentThreadId(id);
    return id;
  }, [setAgentThreadId]);
  // Drag-and-drop a document anywhere in the chat body (§G). Routes to ChatAttachments' with/without-images
  // prompt via its ref. Admin-only + disabled mid-turn (mirrors the attach button).
  const attachRef = useRef<ChatAttachmentsHandle>(null);
  const { dragOver: attachDragOver, dropProps: attachDropProps } = useFileDrop({
    onFiles: (files) => attachRef.current?.acceptFiles(files),
    disabled: !canAttachDocuments || pending,
  });
  // Distinguishes the user's Stop/Esc (tour retired as finished) from mere interruptions
  // (navigation away, closed drawer — persisted as unfinished, resumable).
  const deliberateStopRef = useRef(false);
  // Whether the running tour is /tour-all (Skip section only makes sense across sections).
  const [tourIsAll, setTourIsAll] = useState(false);
  // The open /tour-all section question (chips under the chat). choose(null): at the FIRST
  // question it continues the general tour, at a follow-up it finishes the tour.
  const [tourQuestion, setTourQuestion] = useState<{
    options: string[];
    followUp: boolean;
    choose: (id: string | null) => void;
  } | null>(null);
  const [input, setInput] = useState("");
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { scrollRef, stickRef, onScroll, scrollToBottom, atBottom } =
    useStickToBottom();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // One AG-UI agent per drawer mount (the transport object, not conversation state — sessions are
  // server-authoritative; threadId is re-pointed per send from the store).
  const agentRef = useRef<ReturnType<typeof createAssistantAgent> | null>(null);
  // Running /tour, if any. A ref (not state): the driver owns tourActive in the store.
  const tourRef = useRef<TourController | null>(null);
  // The session the running tour is pinned to (set at launch; bubbles + store go there only).
  const tourThreadRef = useRef<string | null>(null);
  // Section switch = context break: navigating to a different pillar (first path segment,
  // "/" = home) with an EMPTY composer pushes the old conversation out of view — the messages
  // after the break render in a viewport-high wrapper, so the pinned-to-bottom scroll puts the
  // break at the top edge. History stays reachable by scrolling up. Session-local state only.
  const [contextBreak, setContextBreak] = useState<{
    index: number;
    minHeight: number;
  } | null>(null);
  const pillar = location.pathname.split("/")[1] ?? "";
  const prevPillarRef = useRef(pillar);
  useEffect(() => {
    if (pillar === prevPillarRef.current) return;
    prevPillarRef.current = pillar;
    const s = useAgentStore.getState();
    // Draft in progress, tour running, or nothing to push away → leave the scroll alone.
    if (
      open &&
      view === "chat" &&
      !s.tourActive &&
      input.trim() === "" &&
      s.messages.length > 0
    ) {
      setContextBreak({
        index: s.messages.length,
        minHeight: breakMinHeight(scrollRef.current),
      });
      // The old section's follow-ups don't fit the new context — the chips fall back to the
      // new section's starter questions while the fresh context is empty.
      s.setSuggestions([]);
    }
  }, [pillar, open, view, input, scrollRef]);
  // A NEW break gets the animated scroll (the "chat has a new context now" cue). A height
  // re-measurement of an existing break instead RESTORES the bottom pin: when the viewport
  // grows (suggestion chips vanish on send), the browser clamps scrollTop before the larger
  // minHeight lands, so without a re-pin the old conversation peeks back in by the chips'
  // height until the next message re-pins.
  const contextBreakIndex = contextBreak?.index;
  const contextBreakMinHeight = contextBreak?.minHeight;
  const pinnedBreakIndexRef = useRef<number | undefined>(undefined);
  // Custom eased scroll for the section-change break: native scrollTo({behavior:"smooth"}) over a
  // full viewport height fires too fast / lands instantly right after the wrapper insert, so it
  // reads as a hard jump. This animates scrollTop to the bottom over a fixed duration with ease-out.
  const scrollAnimRef = useRef<number | null>(null);
  const animateScrollToBottom = useCallback(
    (durationMs = 500) => {
      const el = scrollRef.current;
      if (!el) return;
      if (scrollAnimRef.current !== null) cancelAnimationFrame(scrollAnimRef.current);
      const start = el.scrollTop;
      const startTime = performance.now();
      const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
      const step = (now: number) => {
        const e = scrollRef.current;
        if (!e) {
          scrollAnimRef.current = null;
          return;
        }
        const target = e.scrollHeight - e.clientHeight; // recompute: content may still grow
        const t = Math.min(1, (now - startTime) / durationMs);
        e.scrollTop = start + (target - start) * easeOut(t);
        if (t < 1) {
          scrollAnimRef.current = requestAnimationFrame(step);
        } else {
          e.scrollTop = e.scrollHeight;
          scrollAnimRef.current = null;
          stickRef.current = true;
        }
      };
      stickRef.current = true;
      scrollAnimRef.current = requestAnimationFrame(step);
    },
    [scrollRef, stickRef],
  );
  useEffect(
    () => () => {
      if (scrollAnimRef.current !== null) cancelAnimationFrame(scrollAnimRef.current);
    },
    [],
  );
  useEffect(() => {
    if (contextBreakIndex === undefined) {
      pinnedBreakIndexRef.current = undefined;
      return;
    }
    if (pinnedBreakIndexRef.current !== contextBreakIndex) {
      pinnedBreakIndexRef.current = contextBreakIndex;
      animateScrollToBottom();
    } else if (stickRef.current && scrollAnimRef.current === null) {
      scrollToBottom("auto");
    }
  }, [
    contextBreakIndex,
    contextBreakMinHeight,
    stickRef,
    scrollToBottom,
    animateScrollToBottom,
  ]);
  // While the fresh context still fits, the wrapper mirrors the live viewport height: chips or
  // a growing composer shrink the blank space instead of scroll-shifting the new context (the
  // bottom pin then holds the break at the top edge without any visible autoscroll).
  const hasContextBreak = contextBreak !== null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!hasContextBreak || !el || typeof ResizeObserver === "undefined")
      return;
    const observer = new ResizeObserver(() => {
      setContextBreak((b) =>
        b && breakMinHeight(el) !== b.minHeight
          ? { ...b, minHeight: breakMinHeight(el) }
          : b,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasContextBreak, scrollRef]);
  // A break outlives its conversation (new chat, switched thread) → drop it.
  useEffect(() => {
    if (contextBreak && messages.length < contextBreak.index)
      setContextBreak(null);
  }, [messages.length, contextBreak]);

  // Current basename-relative path for the tour driver (location from useLocation would be stale
  // inside the driver's async loop).
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  // Person-info clicks accumulate here within a short window; a burst of ≥2 becomes one comparison
  // table, a lone click the rich single profile. `addedIds` mirrors the accumulator for the icon
  // highlight (state → re-render); the ref is what the debounce callback reads.
  const personBatchRef = useRef<Set<string>>(new Set());
  const personBatchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accumulatingRef = useRef(false);
  const [addedIds, setAddedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // User-resizable drawer width (px), desktop only. Drag the left edge to
  // grow toward half the viewport (the cap that keeps the page usable);
  // shrink back down to 280 (smaller and the chat list gets cramped). Mobile
  // ignores this — the overlay sheet is full-width. `wide` widens what is
  // shown for the Grow improve answer without touching the person's own width,
  // so closing the drawer is enough to bring every later opening back to it.
  const [width, setWidth] = useState(DEFAULT_ASSISTANT_WIDTH);
  const wide = useAgentStore((s) => s.wide);
  const clearWide = useAgentStore((s) => s.clearWide);
  const shownWidth = shownAssistantWidth(width, wide);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; w: number } | null>(null);

  const onResizeDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      // A drag on the wide drawer starts from what is on screen. Pressing the grip alone changes
      // nothing: the width only becomes the person's own once they actually move it.
      const start = wide ? clampAssistantWidth(shownWidth, window.innerWidth) : width;
      dragStart.current = { x: e.clientX, w: start };
      setDragging(true);
    },
    [width, wide, shownWidth],
  );
  const onResizeMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const start = dragStart.current;
      if (!start) return;
      // Right-anchored drawer: dragging LEFT grows width. The clamp keeps it
      // between the readable floor and half the viewport.
      const delta = start.x - e.clientX;
      if (delta === 0) return;
      setWidth(clampAssistantWidth(start.w + delta, window.innerWidth));
      // From the first real movement on, the dragged width is the person's own and outlives the
      // improve answer the way any dragged width does.
      clearWide();
    },
    [clearWide],
  );
  const onResizeUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragStart.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  // Abort an in-flight turn only when the drawer unmounts. Closing the drawer
  // does NOT abort — the turn finishes in the background so the agent-service
  // persists it (it only saves completed turns). A running tour DOES abort.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      tourRef.current?.abort();
      if (personBatchTimer.current) clearTimeout(personBatchTimer.current);
    },
    [],
  );

  // Tour abort triggers (see plan): closing the drawer kills its text channel; a route change the
  // driver didn't initiate means the user walked away mid-tour.
  useEffect(() => {
    if (!open) tourRef.current?.abort();
  }, [open]);
  useEffect(() => {
    if (
      tourActive &&
      tourRef.current &&
      !tourRef.current.isExpectedPath(location.pathname)
    ) {
      tourRef.current.abort();
    }
  }, [tourActive, location.pathname]);
  // Leaving the tour's chat mid-run (Chats list, another thread, new chat) is an interruption:
  // abort so no tour bubble ever lands in a foreign chat; the store still goes to the pinned
  // session, which re-offers resume when reopened.
  useEffect(() => {
    if (!tourActive) return;
    if (view !== "chat" || agentThreadId !== tourThreadRef.current)
      tourRef.current?.abort();
  }, [tourActive, view, agentThreadId]);

  // Agent-driven element highlight (HighlightElement, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): find the anchored element,
  // scroll it into view and pulse it, then clear. Retries briefly via rAF-free polling because a
  // NavigateTo in the same turn may still be rendering the target page when the anchor arrives.
  useEffect(() => {
    if (!highlightAnchor) return;
    // Snippet clicks stash their text here (read once — a later click replaces both fields).
    const hint = useAgentStore.getState().highlightHint;
    let el: Element | null = null;
    let tries = 0;
    const find = setInterval(() => {
      el = document.querySelector(
        `[data-assistant-anchor="${highlightAnchor}"]`,
      );
      if (el) {
        clearInterval(find);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("assistant-highlight");
        if (hint) showAnchorHint(el, hint.text);
      } else if (++tries > 10) {
        clearInterval(find); // target never appeared (wrong page) — give up silently
        setHighlightAnchor(null);
      }
    }, 200);
    const clear = setTimeout(() => {
      el?.classList.remove("assistant-highlight");
      setHighlightAnchor(null);
    }, 4500);
    return () => {
      clearInterval(find);
      clearTimeout(clear);
      el?.classList.remove("assistant-highlight");
      clearAnchorHint();
      useAgentStore.getState().setHighlightHint(null); // consumed — never leak into the next pulse
    };
  }, [highlightAnchor, setHighlightAnchor]);

  // ESC: while a tour runs it ends the TOUR (drawer stays open); otherwise it closes the drawer
  // (non-modal, so we listen globally while open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (useAgentStore.getState().tourActive) {
        deliberateStopRef.current = true;
        tourRef.current?.abort();
      } else setOpen(false);
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  // Load the Chats list whenever the list view is shown (and refetch when a
  // background turn finishes — `pending` flips false — so a just-saved chat
  // appears).
  useEffect(() => {
    if (!open || view !== "list") return;
    const ctrl = new AbortController();
    setThreadsState("loading");
    listAgentThreads(ctrl.signal)
      .then((t) => {
        setThreads(t);
        setThreadsState("idle");
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setThreadsState("error");
      });
    return () => ctrl.abort();
  }, [open, view, pending, setThreads, setThreadsState]);

  useEffect(() => {
    // Follow new content (streamed tokens; and `pending` flips so the turn-end re-scroll keeps the last
    // block visible once the suggestion chips shrink the viewport) — but ONLY while pinned to the bottom,
    // so a reader who scrolled up isn't yanked back. Skip while mid-selection (accumulating person clicks)
    // so we don't pull the reply's person links out of reach. Instant follow: see useStickToBottom.
    if (view === "chat" && !accumulatingRef.current && stickRef.current) {
      scrollToBottom("auto");
    }
  }, [messages, view, pending, stickRef, scrollToBottom]);

  // Entering/opening the chat view (open, new chat, opened thread, list→chat) re-pins to the latest, even
  // if the user had scrolled up in a previous view. Deliberately keyed on [open, view] only — NOT on
  // agentThreadId, which the "started" SSE event flips mid-stream on a new chat's first turn (that would
  // yank a scrolled-up reader). Also focuses the composer so the user can type right away.
  useEffect(() => {
    if (open && view === "chat") {
      scrollToBottom("auto");
      inputRef.current?.focus();
    }
  }, [open, view, scrollToBottom]);

  // Composer sizing. JS autosize instead of `field-sizing: content` because Chrome's resize grip
  // loses against content sizing (drags snap back). A handle drag pins the height for the current
  // draft; sending, clearing, or starting a new draft unpins (see onChange + handleSend).
  const manualResizeRef = useRef(false);
  const autoGrowInput = useCallback(() => {
    const el = inputRef.current;
    if (!el || manualResizeRef.current) return;
    el.style.height = "auto";
    // Autosize caps at ~8 lines and the field scrolls internally; a manual drag may go further
    // (up to the CSS max-h). CSS min-h clamps the low end.
    el.style.height = `${Math.min(el.scrollHeight, 224)}px`;
  }, []);

  // Top-edge drag handle. The native `resize` grip is unusable on a bottom-anchored composer
  // (its drag-down-to-grow semantics invert: the box grows upward), so the handle maps
  // drag-up = taller directly. A drag pins the height until the input is cleared by sending.
  const startComposerResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = inputRef.current;
      if (!el) return;
      e.preventDefault();
      const handle = e.currentTarget;
      const startY = e.clientY;
      const startHeight = el.offsetHeight;
      handle.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent) => {
        // CSS min-h/max-h clamp the result.
        el.style.height = `${startHeight + (startY - ev.clientY)}px`;
        manualResizeRef.current = true;
      };
      const onUp = () => {
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
    },
    [],
  );

  // The composer growing (autosize) or being grip-dragged shrinks the flex-1 message list from
  // below. ResizeObserver fires after layout (unlike onChange, which runs before the height
  // changes), so a pinned view re-pins for typing growth and manual drags alike.
  useEffect(() => {
    const el = inputRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottom("auto");
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [view, stickRef, scrollToBottom]);

  const errorText = intl.formatMessage({
    id: "agent.error",
    defaultMessage: "Couldn't reach the assistant. Try again.",
  });

  // Launch a guided tour (/tour = current page, /tour-all = onboarding intro + the main pages).
  // Deterministic — no LLM, no thread needed. Desktop-first: the pillar sidebar the intro
  // highlights only exists at lg+ (and on mobile the full-screen drawer covers the page), so
  // below 1024px we post a notice instead of running.
  const runTourCommand = useCallback(
    // resume: continue an interrupted tour, silently (no re-posted command bubble).
    // route set = start at that page, skipping its shown summary + stops through `anchor`
    // (freshPage runs the page whole instead — the Skip-section entry). route null = the
    // interruption was in the INTRO; it replays from the item after `anchor`.
    (
      command: string,
      resume?: {
        route: string | null;
        anchor: string | null;
        freshPage?: boolean;
      },
    ) => {
      deliberateStopRef.current = false;
      if (!resume) appendMessage("user", command);
      if (!globalThis.matchMedia?.("(min-width: 1024px)").matches) {
        appendMessage(
          "assistant",
          intl.formatMessage({
            id: "agent.tour.mobileNotice",
            defaultMessage:
              "Tours highlight elements on the page next to the chat, so they need a larger screen. Please try it on a desktop window.",
          }),
        );
        return;
      }
      const routes =
        command === "/tour-all"
          ? resume?.route
            ? TOUR_ALL_ROUTES.slice(
                Math.max(0, TOUR_ALL_ROUTES.indexOf(resume.route)),
              )
            : TOUR_ALL_ROUTES
          : [resume?.route ?? pathnameRef.current];
      setTourIsAll(command === "/tour-all");
      setResumeTour(null); // the controls flip to the live tour's state
      // Any fresh app tour retires the topbar onboarding hint (server log; fire-and-forget).
      if (command === "/tour-all" && !resume) {
        void postTourHintEvent("started").catch(() => undefined);
      }
      // Pin the tour to the chat it started in (minted up front on a fresh chat). Everything the
      // run does — bubbles, pause-Q&A, the final store — belongs to THIS session; switching to
      // another chat mid-run aborts the tour as an interruption (effect below).
      const tourThreadId =
        useAgentStore.getState().agentThreadId ??
        (() => {
          const id = crypto.randomUUID();
          setAgentThreadId(id);
          return id;
        })();
      tourThreadRef.current = tourThreadId;
      const pillarDescriptions: Record<PillarId, string> = {
        save: intl.formatMessage({
          id: "agent.tour.pillar.save",
          defaultMessage:
            "SAVE is your personal data vault: your profile, CV entries, documents, contacts and pinboard.",
        }),
        grow: intl.formatMessage({
          id: "agent.tour.pillar.grow",
          defaultMessage:
            "GROW is your power playground: assess your 30 powers, watch learning content and play your way to a sharper profile.",
        }),
        share: intl.formatMessage({
          id: "agent.tour.pillar.share",
          defaultMessage:
            "SHARE is the community feed: publish projects and posts, gather interested people and feedback.",
        }),
        match: intl.formatMessage({
          id: "agent.tour.pillar.match",
          defaultMessage:
            "MATCH builds teams: you define the requirements, the solver picks the right people.",
        }),
      };
      // Collect the tour's messages (flat, for the model) AND the structured steps (for the
      // interactive reload outline) so the walkthrough persists as one synthetic turn (@adr SPARK-0014-design-primitives-single-source@1).
      const transcript: string[] = [];
      const steps: TourStepPayload[] = [];
      // The last shown placed step (page stop/summary, or intro item with route null) — the
      // resume cursor if this run gets interrupted.
      let lastPlaced: { route: string | null; anchor: string | null } | null =
        null;
      tourRef.current = startTour(
        routes,
        {
          navigate,
          currentPath: () => pathnameRef.current,
          post: (content, color, indent, meta) => {
            transcript.push(content);
            if (meta) {
              steps.push({
                content,
                indent: indent ?? 0,
                section: meta.section,
                anchor: meta.anchor,
                route: meta.route,
              });
            }
            if (meta?.route || meta?.anchor) {
              lastPlaced = {
                route: meta.route ?? null,
                anchor: meta.anchor ?? null,
              };
            }
            // The returned id links the bubble to its tour step (click → revisit). Meta rides onto
            // the live message too, so snippets stay clickable after the tour ends.
            return appendMessage("assistant", content, {
              color,
              indent,
              section: meta?.section,
              anchor: meta?.anchor,
              route: meta?.route,
            });
          },
          // Resumed tours pass the intro ONLY when the interruption happened inside it (route-less
          // cursor) — the driver then fast-forwards to the unseen items. Page cursors skip it whole.
          intro:
            command === "/tour-all" && (!resume || resume.route === null)
              ? [
                  // The main page first: the logo is how you always get back to it.
                  {
                    anchor: "nav.home",
                    description: intl.formatMessage({
                      id: "agent.tour.pillar.home",
                      defaultMessage:
                        "The Smycles logo always takes you to the main page: your dashboard with the latest numbers from every section.",
                    }),
                  },
                  ...PILLARS_META.map(({ id, anchor }) => ({
                    anchor,
                    description: pillarDescriptions[id],
                  })),
                ]
              : undefined,
          // After the sections (general path only): the topbar in a neutral tone.
          introTopbar:
            command === "/tour-all" && (!resume || resume.route === null)
              ? (
                  [
                    [
                      "topbar.search",
                      intl.formatMessage({
                        id: "agent.tour.topbar.search",
                        defaultMessage:
                          "The global search: find people, projects and posts from anywhere in the app.",
                      }),
                    ],
                    [
                      "topbar.assistant",
                      intl.formatMessage({
                        id: "agent.tour.topbar.assistant",
                        defaultMessage:
                          "The AI assistant: this button opens and closes the chat you are reading right now.",
                      }),
                    ],
                    [
                      "topbar.chat",
                      intl.formatMessage({
                        id: "agent.tour.topbar.chat",
                        defaultMessage:
                          "Messages: direct chats with your contacts.",
                      }),
                    ],
                    [
                      "topbar.notifications",
                      intl.formatMessage({
                        id: "agent.tour.topbar.notifications",
                        defaultMessage:
                          "Notifications: invitations, updates and activity around you land here.",
                      }),
                    ],
                    [
                      "topbar.user",
                      intl.formatMessage({
                        id: "agent.tour.topbar.user",
                        defaultMessage:
                          "Your account: click your name for the profile menu, settings and logout.",
                      }),
                    ],
                  ] as const
                ).map(([anchor, description]) => ({ anchor, description }))
              : undefined,
          // "Try it yourself" after the finish, scoped to the sections that were actually toured
          // (a pick of only Share + Match yields only the project and match suggestions).
          // Internal links render as section-colored chips (AgentMarkdown), one per line —
          // matching the section-question chips. ?service=linkedin auto-starts the importer.
          finishExtra: (toured) => {
            const items: string[] = [];
            if (toured.includes("save")) {
              items.push(
                `[${intl.formatMessage({ id: "agent.tour.next.profile", defaultMessage: "Fill out your profile" })}](/save/basics)`,
                `[${intl.formatMessage({ id: "agent.tour.next.linkedin", defaultMessage: "Import your data via LinkedIn" })}](/save/basics?service=linkedin)`,
              );
            }
            if (toured.includes("grow")) {
              items.push(
                `[${intl.formatMessage({ id: "agent.tour.next.quiz", defaultMessage: "Take a power quiz" })}](/grow/play)`,
              );
            }
            if (toured.includes("share")) {
              items.push(
                `[${intl.formatMessage({ id: "agent.tour.next.project", defaultMessage: "Create a project" })}](/share)`,
              );
            }
            if (toured.includes("match")) {
              // ?create=1: MatchPage creates the match and opens its editor directly.
              items.push(
                `[${intl.formatMessage({ id: "agent.tour.next.match", defaultMessage: "Create a match" })}](/match?create=1)`,
              );
            }
            if (!items.length) return null;
            return [
              intl.formatMessage({
                id: "agent.tour.next.heading",
                defaultMessage: "Now try it yourself:",
              }),
              ...items,
            ].join("\n\n");
          },
          // Fresh runs point a hint card at each tour control button right after the welcome.
          controlsIntro:
            command === "/tour-all" && !resume
              ? [
                  {
                    anchor: "tour.back",
                    hint: intl.formatMessage({
                      id: "agent.tour.buttonHint.back",
                      defaultMessage: "Back: revisit the previous step.",
                    }),
                  },
                  {
                    anchor: "tour.pause",
                    hint: intl.formatMessage({
                      id: "agent.tour.buttonHint.pause",
                      defaultMessage: "Pause: halt the tour and ask questions.",
                    }),
                  },
                  {
                    anchor: "tour.next",
                    hint: intl.formatMessage({
                      id: "agent.tour.buttonHint.next",
                      defaultMessage: "Next: continue right away.",
                    }),
                  },
                  {
                    anchor: "tour.skipSection",
                    hint: intl.formatMessage({
                      id: "agent.tour.buttonHint.skipSection",
                      defaultMessage: "Skip section: jump to the next area.",
                    }),
                  },
                  {
                    anchor: "tour.stop",
                    hint: intl.formatMessage({
                      id: "agent.tour.buttonHint.stop",
                      defaultMessage: "Stop or Esc: end the tour.",
                    }),
                  },
                ]
              : undefined,
          // Section question after the pillar intro: the composer shows the four section chips
          // plus Continue; no click within the timeout means the general tour just goes on.
          ask: (options, followUp) =>
            new Promise((resolve) => {
              const timer = setTimeout(() => {
                setTourQuestion(null);
                resolve(null);
              }, TOUR_QUESTION_TIMEOUT_MS);
              setTourQuestion({
                options,
                followUp,
                choose: (id) => {
                  clearTimeout(timer);
                  setTourQuestion(null);
                  resolve(id);
                },
              });
            }),
          texts: {
            escHint: intl.formatMessage({
              id: "agent.tour.escHint",
              defaultMessage:
                "_Use the buttons below the chat: back, pause, next and skip section. Press **Esc** or the stop button anytime to end the tour._",
            }),
            noTour: intl.formatMessage({
              id: "agent.tour.noTour",
              defaultMessage: "There is no tour for this page yet.",
            }),
            finished: intl.formatMessage({
              id: "agent.tour.finished",
              defaultMessage:
                "That's the end of the tour. Ask me anything about what you saw!",
            }),
            welcome: intl.formatMessage({
              id: "agent.tour.welcome",
              defaultMessage:
                "Welcome to the Smycles system. This tour presents the important features. The buttons under the chat steer the tour. You can also click any chat snippet to jump back to its spot.",
            }),
            sectionsIntro: intl.formatMessage({
              id: "agent.tour.sectionsIntro",
              defaultMessage:
                "I'll quickly show you the sections before we go into details.",
            }),
            question: intl.formatMessage({
              id: "agent.tour.sectionQuestion",
              defaultMessage:
                "Should I show you one section first? Pick it below the chat, otherwise I'll present the main page and every section one after another.",
            }),
            questionNext: intl.formatMessage({
              id: "agent.tour.sectionQuestionNext",
              defaultMessage:
                "That section is done. Should I show you another one? Pick it below the chat, otherwise the tour ends here.",
            }),
          },
        },
        resume && !resume.freshPage
          ? { skipUntil: { route: resume.route, anchor: resume.anchor } }
          : undefined,
      );
      // Persist whatever was shown, exactly once, at the FIRST of: content complete (natural
      // finish or early abort — NOT done, which review mode keeps pending until Stop/Esc) or the
      // page unloading mid-tour (pagehide → keepalive fetch, the beacon that can carry the CSRF
      // header). `finished:false` marks an interruption — the thread then offers to resume.
      // The tour can be the very first interaction — mint the thread id then, so a follow-up
      // chat continues the same thread. Fire-and-forget: a failed store never disturbs the UI.
      let persisted = false;
      const persist = (keepalive: boolean, finished: boolean) => {
        if (persisted || !transcript.length) return;
        persisted = true;
        // Always the PINNED session — never whatever chat happens to be open at store time.
        postTourTranscript(
          tourThreadId,
          command,
          transcript.join("\n\n"),
          steps,
          {
            keepalive,
            finished,
          },
        ).catch((e) => console.debug("[tour] transcript not stored", e));
      };
      // ponytail: bfcache restore after a mid-tour pagehide skips the later full store — rare,
      // and the partial transcript is already saved.
      const onPagehide = () => persist(true, false);
      window.addEventListener("pagehide", onPagehide);
      void tourRef.current.ended.then(() => {
        setTourQuestion(null); // an abort mid-question must not leave stale chips behind
        window.removeEventListener("pagehide", onPagehide);
        // tourFinished still holds the natural-finish truth here (the driver resolves `ended`
        // after settling the flags); Stop/Esc also retires the tour as finished.
        const finished =
          useAgentStore.getState().tourFinished || deliberateStopRef.current;
        persist(false, finished);
        // Interrupted → offer to continue, but only while the tour's OWN chat is still the one
        // loaded (mirrors what its session detail reports; a route-less cursor means restart).
        // After a switch to another chat the offer comes back via that detail on reopen.
        if (
          !finished &&
          steps.length &&
          useAgentStore.getState().agentThreadId === tourThreadId
        ) {
          setResumeTour({
            command,
            route: lastPlaced?.route ?? null,
            anchor: lastPlaced?.anchor ?? null,
          });
        }
      });
    },
    [appendMessage, intl, navigate, setAgentThreadId, setResumeTour],
  );

  // Continue the loaded session's interrupted tour from its cursor. skipSection instead jumps
  // to the first page of the NEXT pillar (fresh page: its summary posts normally).
  const continueTour = useCallback(
    (
      resume: NonNullable<
        ReturnType<typeof useAgentStore.getState>["resumeTour"]
      >,
      skipSection = false,
    ) => {
      const route = resume.route ?? null;
      const anchor = resume.anchor ?? null;
      if (skipSection && resume.command === "/tour-all") {
        // From a page cursor: the next pillar's first page. From an intro cursor: skipping the
        // section means skipping the rest of the intro — the first page. Both run whole.
        const next = route
          ? TOUR_ALL_ROUTES.slice(TOUR_ALL_ROUTES.indexOf(route) + 1).find(
              (r) => r.split("/")[1] !== route.split("/")[1],
            )
          : TOUR_ALL_ROUTES[0];
        if (!next) {
          // Interrupted in the last section — nothing after it to skip to.
          setResumeTour(null);
          return;
        }
        runTourCommand(resume.command, {
          route: next,
          anchor: null,
          freshPage: true,
        });
        return;
      }
      if (!route && !anchor) {
        // Cursor without any position (nothing placed was shown) — restart the tour whole.
        runTourCommand(resume.command);
        return;
      }
      runTourCommand(resume.command, { route, anchor });
    },
    [runTourCommand, setResumeTour],
  );

  // Click on a tour snippet: while its tour is running, delegate to the driver (full revisit —
  // pause, spotlight, self-heal); otherwise (reloaded thread, or tour already ended) the simple
  // mechanism: navigate to the step's page and pulse its element via the highlightAnchor effect.
  const revisitSnippet = useCallback(
    (msg: AgentMessage) => {
      const { tourActive: touring, tourStepIds: stepIds } =
        useAgentStore.getState();
      if (touring && stepIds.includes(msg.id)) {
        tourRef.current?.revisitMessage(msg.id);
        return;
      }
      if (msg.route && pathnameRef.current !== msg.route) navigate(msg.route);
      if (msg.anchor) {
        // The snippet rides along as a floating card at the pulsed element (highlight effect).
        setHighlightHint({ text: msg.content });
        setHighlightAnchor(msg.anchor);
      }
    },
    [navigate, setHighlightAnchor, setHighlightHint],
  );

  const handleSend = useCallback(
    async (textArg?: string, action?: AgentAction) => {
      const text = (textArg ?? input).trim();
      if (!text || pending) return;
      // Taken and cleared up front: they belong to this send and to no other.
      const followUps = turnFollowUps.current;
      turnFollowUps.current = null;
      // Exact slash commands only (case-insensitive) — everything else goes to the model.
      const command = text.toLowerCase();
      const isTourCommand = command === "/tour" || command === "/tour-all";
      // Shows or hides the company-search control. A command rather than a permanent button because
      // the route is a diagnostic for the rare question the door gets wrong, not something every
      // reader needs beside every question (@adr SPARK-0051-the-company-search-mode-is-a-setting@1).
      if (command === "/company-search-mode") {
        setInput("");
        manualResizeRef.current = false;
        if (inputRef.current) inputRef.current.style.height = "";
        useAgentStore.getState().toggleCompanySearchMode();
        return;
      }
      // A send while the tour is RUNNING ends it (escape hatch). While PAUSED the tour survives:
      // the message goes to the model as a normal question and the user can continue afterwards.
      // A tour command always restarts (never two tours at once).
      const { tourActive: touring, tourPaused: pausedNow } =
        useAgentStore.getState();
      if (touring && (isTourCommand || !pausedNow)) tourRef.current?.abort();
      if (isTourCommand) {
        setInput("");
        // Sending clears any manual grip resize (here and below): back to a one-row field.
        manualResizeRef.current = false;
        if (inputRef.current) inputRef.current.style.height = "";
        scrollToBottom("auto");
        runTourCommand(command);
        return;
      }
      setInput("");
      manualResizeRef.current = false;
      if (inputRef.current) inputRef.current.style.height = "";
      // Sending always snaps to the bottom (you want to see your message + the reply), even if you'd
      // scrolled up. Re-pins here; the autoscroll effect does the real scroll once the new messages render.
      scrollToBottom("auto");
      // On the match editor, refine `match` to `match/setup` | `match/results` (the active step,
      // published by MatchDetailPage) so the assistant gets the setup vs results tool split.
      const baseSection = sectionForPath(location.pathname);
      const section =
        baseSection === "match" && matchSubsection ? `match/${matchSubsection}` : baseSection;
      // Tool-binding context: on a focused match route (/match/:matchId) send {matchId} so
      // agent-service offers that match's write tools (rename/team size/notes/requirements/brief).
      // Without it every match-write tool is gated out (requiredContext=matchId).
      const bindingContext = agentBindingContextForPath(location.pathname);
      // The open Studio notebook rides every Studio turn as data; nowhere else.
      const studioNotebook = section === "save/studio" ? useAgentStore.getState().studioNotebook : null;
      const assistantId = startTurn(text, section);
      const controller = new AbortController();
      abortRef.current = controller;
      // Assistant-driven refetch (@adr SPARK-0024-assistant-driven-refetch-is-mutation-level@1): collect the turn's tool names; after the run,
      // refetch the queries mapped to data-changing tools. Only tools whose result was NOT an
      // error count — a failed write (isError) must not trigger the refetch + editor reconcile,
      // else a rejected SetMatch* refetches MatchDetail and rebases the editor against data that
      // never changed. Correlate result→name by toolCallId.
      const toolNameById = new Map<string, string>();
      const succeededTools: string[] = [];
      let solverProducedDraft = false;
      let failed = false;
      // Refetch the queries mapped to this turn's succeeded tools and nudge an open match editor to
      // reconcile. Called after the run regardless of whether the turn failed (#1) — server state
      // moved for every succeeded tool. Reads succeededTools/solverProducedDraft at call time.
      const reconcileCommittedWrites = () => {
        // One refetchQueries call → all fire in parallel; the Set in queriesFor dedups.
        const qs = queriesFor(succeededTools);
        if (qs.length) void apolloClient.refetchQueries({ include: qs });
        // A design-write refetches MatchDetail — tell an open match editor to reconcile its local
        // overlay with the server truth, else its stale autosave clobbers the agent's change.
        if (qs.includes("MatchDetail")) signalAgentWrite();
        // The solver ran for the BOUND match — its result is a persisted draft; tell THAT match's
        // editor (matchId-scoped, #3) to show the Result step and LOAD the draft (no re-solve).
        if (solverProducedDraft && bindingContext?.matchId) {
          signalMatchSolve(bindingContext.matchId);
        }
        if (!qs.length) {
          console.debug("[agent-refresh] no data-changing tools this turn – no refetch");
        }
      };
      try {
        await runAguiTurn(
          (agentRef.current ??= createAssistantAgent()),
          {
            message: text,
            section,
            bindingContext,
            // Typed intent of a button-started turn; undefined for anything typed into the composer.
            action,
            searchSources: [useAgentStore.getState().takeSearchSource()].filter((source) => source !== null),
            // NOT taken: unlike the globe, the company route holds until the reader changes it.
            companyRoute: useAgentStore.getState().companyRoute,
            labelThreshold: useAgentStore.getState().labelThreshold,
            studioNotebook,
            threadId: agentThreadId,
            // Motive 1 (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): what the user currently sees — the current route (its path
            // carries params too, e.g. /grow/power/CRE) plus whatever the mounted page(s)
            // registered via useAssistantContext. Read imperatively (getState) so page-data churn
            // never re-renders the drawer.
            uiContext: [
              {
                description: "current route in the app",
                value: location.pathname,
              },
              ...Object.values(useAgentStore.getState().uiContext).flat(),
            ],
            abortController: controller,
          },
          {
            onThreadId: setAgentThreadId,
            // What a Stop names. Minted per run by @ag-ui/client and echoed in RUN_STARTED.
            onRunId: setRunId,
            onDelta: (delta) => appendDelta(assistantId, delta),
            onToolCall: (name, args, id) => {
              toolNameById.set(id, name);
              // Agent-driven UI (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): navigate/highlight fire mid-stream, validated
              // against the client-side allowlists. (UI tools aren't refetch-mapped, so they
              // don't participate in the success-gating below.)
              executeUiTool(name, args, {
                navigate: (path) => navigate(path),
                highlight: setHighlightAnchor,
                // Lets OpenMatch switch step in the match the user currently has open (matchId omitted).
                currentMatchId: location.pathname.match(/^\/match\/([^/?]+)/)?.[1] ?? null,
                showCard: (card) => addCard(assistantId, { ...card, id }),
                studioNotebook: studioNotebook ?? undefined,
              });
            },
            onToolResult: (id, result) => {
              if (result.isError) return;
              const name = toolNameById.get(id);
              if (name) succeededTools.push(name);
              // A successful solve means a draft was persisted server-side. Don't gate on
              // result.resultId (a fragile regex extraction agent-side) — the editor loads the draft
              // by matchId, so the tool succeeding is signal enough (#2).
              if (name === "GenerateTransientMatchResult") {
                solverProducedDraft = true;
              }
            },
            // A turn that came with its own follow-ups ignores the generated ones; otherwise they
            // would land first and be replaced a moment later, which reads as flicker.
            onSuggestions: followUps ? () => {} : setSuggestions,
            onProgress: addProgress,
            onNotice: (code) => useAgentStore.getState().setNotice(assistantId, code),
            // The answer researched the public web: flag it, so the external-content notice below the
            // composer stays with this thread (and its reload) instead of vanishing with the progress trace.
            onRunResult: (result) => {
              if (result.externalSources) markExternalSources(assistantId);
            },
            // Never render the agent-service's raw error text (@adr SPARK-0010-no-raw-server-errors-in-ui@1); always show our
            // localized copy.
            onError: () => {
              failed = true;
              failTurn(assistantId, errorText);
            },
          },
        );
        // Reconcile committed writes whether or not the turn's text answer failed (#1): every
        // succeeded tool already persisted server-side, so its queries must refetch and an open
        // editor must reconcile even when a later error fails the turn — otherwise the client shows
        // stale data until the next manual action. endTurn() alone stays gated on success below;
        // on failure failTurn already ran in onError.
        reconcileCommittedWrites();
        if (!failed) {
          // Hand the turn's progress lines to the answer before they are cleared, so the finished
          // message can show what produced it.
          endTurn(assistantId);
          // Only after a turn that actually answered: offering to save or export a result that
          // failed to arrive would be wrong.
          if (followUps) setSuggestions(followUps);
        }
      } catch {
        if (controller.signal.aborted) {
          endTurn();
        } else {
          // A hard throw (not the onError path) may still follow a successful write — reconcile it
          // before failing the turn so a committed change isn't stranded (#1). Aborts skip this: the
          // user cancelled, so don't churn refetches.
          reconcileCommittedWrites();
          if (!failed) failTurn(assistantId, errorText);
        }
      }
    },
    [
      input,
      pending,
      startTurn,
      location.pathname,
      navigate,
      agentThreadId,
      appendDelta,
      endTurn,
      failTurn,
      setAgentThreadId,
      setSuggestions,
      setHighlightAnchor,
      errorText,
      scrollToBottom,
      runTourCommand,
      apolloClient,
      matchSubsection,
      signalAgentWrite,
      signalMatchSolve,
    ],
  );

  // Drain a prompt queued by `ask()` (e.g. the power-detail button): send it once no turn is in flight.
  /**
   * Stop the running turn.
   *
   * TWO things, and both are needed. The POST stops the WORK: agent-service runs the loop to
   * completion on client disconnect, so abandoning the stream alone would leave the model calls and
   * database queries running on the user's behalf. The local abort stops the LISTENING, so the
   * drawer settles at once instead of waiting for the server's stream to close.
   *
   * The turn is persisted upstream as `stopped`, with the question kept — so the thread that comes
   * back on reload carries it, and the next message can refer to it.
   */
  const handleStop = useCallback(async () => {
    const runId = useAgentStore.getState().runId;
    if (runId) await stopAgentRun(runId);
    // NO local abort. The server ends the stream itself — it stops the loop and sends the stopped
    // notice followed by RUN_FINISHED. Aborting the transport here raced that: the turn ended at the
    // browser before any terminal frame arrived, and the drawer reported the assistant as
    // unreachable over a turn the user had deliberately stopped (2026-10-09; five server-side fixes
    // changed nothing, which is what finally placed the fault on this side).
  }, []);

  // The `!pending` guard queues a click made mid-stream; clearing before the send prevents a re-fire.
  // `handleSend` reads the current route for `section`, and `agentThreadId` (null for a new chat, or the
  // existing id when `ask` appended) — so section + thread are correct without extra plumbing.
  useEffect(() => {
    if (pendingSend && !pending) {
      const { text, action, followUps } = pendingSend;
      clearPendingSend();
      // Read by handleSend for this one turn and cleared there, so a later message the person
      // types never inherits chips that were chosen for an answer it is not.
      turnFollowUps.current = followUps ?? null;
      void handleSend(text, action);
    }
  }, [pendingSend, pending, handleSend, clearPendingSend]);

  // Stream a profile load into a distinct info block (@adr SPARK-0014-design-primitives-single-source@1). No LLM. One id → the rich
  // single-person profile; ≥2 → a comparison table. Reuses the streaming store actions; on
  // failure shows the localized error, never the raw upstream text.
  const runProfileLoad = useCallback(
    async (ids: string[]) => {
      if (!agentThreadId || ids.length === 0) return;
      const infoId = startInfoStream();
      const controller = new AbortController();
      abortRef.current = controller;
      const onEvent = (e: AgentEvent) => {
        if (e.kind === "token" && e.delta) appendDelta(infoId, e.delta);
        else if (e.kind === "done") endTurn();
        else if (e.kind === "error") failTurn(infoId, errorText);
      };
      try {
        if (ids.length === 1)
          await streamPersonDetail(
            agentThreadId,
            ids[0]!,
            onEvent,
            controller.signal,
          );
        else
          await streamPersonsTable(
            agentThreadId,
            ids,
            onEvent,
            controller.signal,
          );
        endTurn();
      } catch {
        if (controller.signal.aborted) endTurn();
        else failTurn(infoId, errorText);
      }
    },
    [agentThreadId, startInfoStream, appendDelta, endTurn, failTurn, errorText],
  );

  // Info-link click: accumulate within the debounce window (each click resets it), so clicking
  // several people becomes ONE request. Clicks while a load is already streaming are dropped; the
  // clicked icons show an "added" highlight while accumulating. The window close fires the load.
  // A person chosen among a reply's candidates is the user's answer in this chat, and remembered, so a card
  // that looks the name up again (the organizational record's draft) picks the same person.
  const choosePersonInChat = useCallback(
    (choice: PersonChoice) => {
      const store = useAgentStore.getState();
      if (store.pending) return;
      store.choosePerson(choice.asked, choice.id);
      store.ask(personChosenMessage(intl, choice));
    },
    [intl],
  );
  const handlePersonInfo = useCallback(
    (personId: string) => {
      if (pending) return;
      personBatchRef.current.add(personId);
      accumulatingRef.current = true;
      setAddedIds(new Set(personBatchRef.current));
      if (personBatchTimer.current) clearTimeout(personBatchTimer.current);
      personBatchTimer.current = setTimeout(() => {
        const ids = [...personBatchRef.current];
        personBatchRef.current = new Set();
        accumulatingRef.current = false; // selection done → the load's stream may scroll normally
        setAddedIds(new Set());
        void runProfileLoad(ids);
      }, PERSON_BATCH_DEBOUNCE_MS);
    },
    [pending, runProfileLoad],
  );

  // 3 random starters from the section's pool of 10, reshuffled only when the
  // section (or locale) changes. Done in an effect (not render) because Math.random
  // is impure. ponytail: sort-by-random is biased but fine for cosmetic variety.
  const section = sectionForPath(location.pathname);
  const [starterPicks, setStarterPicks] = useState<string[]>([]);
  useEffect(() => {
    setStarterPicks(
      [...starterPrompts(intl, section)]
        .sort(() => Math.random() - 0.5)
        .slice(0, 3),
    );
  }, [intl, section]);

  // Chips above the composer: agent follow-ups once a reply has them, else the
  // random starters in an empty chat, else nothing. Capped at 3. An empty fresh
  // context after a section switch shows the new section's starters UNCONDITIONALLY —
  // a turn finishing after the switch would otherwise repopulate the old section's
  // follow-ups past the setSuggestions([]) in the break effect.
  const freshContext =
    contextBreak !== null && contextBreak.index >= messages.length;
  const chips = freshContext
    ? starterPicks
    : suggestions.length
      ? suggestions.slice(0, 3)
      : messages.length === 0
        ? starterPicks
        : [];

  // External-content notice under the composer (@external-adr AGENT-SERVICE-0034-web-search-as-a-separate-tool):
  // shown from the moment a web search runs, and kept once an answer in this thread carries web
  // content. The flag rides on the message, so a reopened thread shows the notice where the live one did.
  const showExternalContentNotice =
    (pending && progress.some((p) => p.phase === "searching_web" || p.phase === "searching_pubmed")) ||
    messages.some((m) => m.externalSources);

  const handleOpenThread = useCallback(
    async (id: string) => {
      setLoadError(false);
      setLoadingId(id);
      try {
        const detail = await fetchAgentThread(id);
        openThread(detail);
        // The break's index belongs to the previous conversation.
        setContextBreak(null);
      } catch {
        setLoadError(true);
      } finally {
        setLoadingId(null);
      }
    },
    [openThread],
  );

  const handleConfirmDelete = useCallback(async () => {
    const id = confirmDeleteId;
    if (!id) return;
    setConfirmDeleteId(null);
    setLoadError(false);
    setDeletingId(id);
    try {
      await deleteAgentThread(id);
      removeThread(id);
    } catch {
      setLoadError(true);
    } finally {
      setDeletingId(null);
    }
  }, [confirmDeleteId, removeThread]);

  // One message bubble; shared by the pre-/post-context-break slices below.
  const renderMessage = (msg: (typeof messages)[number]) => {
    const isMine = msg.role === "user";
    const isInfo = msg.role === "info";
    const content = msg.noticeCode ? agentNoticeMessage(intl, msg.noticeCode) : msg.content;
    const isAwaiting = pending && !isMine && content === "" && !msg.error;
    // While a tour runs, its step bubbles are clickable: click → revisit that
    // step (navigate back + re-highlight). Clickability comes from the store
    // mirror (tourStepIds), not the controller ref (refs must not be read
    // during render).
    const revisitable =
      !isMine && !isInfo && tourActive && tourStepIds.includes(msg.id);
    // Persisted tour snippets (reloaded threads, or after the live tour ended):
    // clicking navigates to the step's page and pulses its element — the simple
    // highlightAnchor mechanism, no driver needed.
    const persistedClick =
      !revisitable && !isMine && !isInfo && Boolean(msg.anchor || msg.route);
    const clickable = revisitable || persistedClick;
    // The step currently on screen — extra "you are here" ring so clicking
    // around the snippets always shows which one is being presented.
    const isCurrentStep = revisitable && msg.id === tourCurrentStepId;
    // Tour snippets (live or persisted outline) carry an accent border; NORMAL
    // answers get a tiny textual section tag instead — identification is
    // textual, the old per-section colors are gone.
    const isTourSnippet =
      !isMine &&
      !isInfo &&
      (msg.anchor != null || msg.route != null || msg.indent != null);
    const sectionTag =
      !isMine &&
      !isInfo &&
      !isTourSnippet &&
      msg.section &&
      msg.section !== "default"
        ? (msg.section === "growth" ? "grow" : msg.section)
            .toUpperCase()
            .replace("/", " · ")
        : null;
    return (
      <div
        key={msg.id}
        className={`flex ${isMine ? "justify-end" : "justify-start"}`}
        // Tour outline: section intros flush left, page summaries and stops
        // indented one/two levels so the hierarchy is scannable.
        style={msg.indent ? { paddingLeft: msg.indent * 16 } : undefined}
      >
        <div className={isInfo ? "w-full" : "max-w-[85%]"}>
          {/* Textual section identification for normal answers (replaces the
              old colored border): "GROW", "SAVE · PROFILE", … */}
          {sectionTag && (
            <div className="mb-0.5 px-1 text-smycles-micro font-medium tracking-[0.15em] text-ink-muted">
              {sectionTag}
            </div>
          )}
          {isAwaiting ? (
            <ProgressTrace steps={progress} />
          ) : isMine ? (
            <div className="whitespace-pre-wrap rounded-lg bg-ink px-3 py-2 text-smycles-body leading-relaxed text-ink">
              {visibleUserText(msg.content)}
            </div>
          ) : isInfo ? (
            <div className="rounded-lg border border-accent-decoration/30 bg-accent/[0.04] px-3 py-2 text-smycles-body leading-relaxed text-ink">
              <div className="mb-1 flex items-center gap-1 text-smycles-label font-medium tracking-wide text-ink-muted">
                <InfoGlyph />
                <FormattedMessage
                  id="agent.personInfo.heading"
                  defaultMessage="Profile"
                />
              </div>
              <AgentMarkdown
                content={content}
                onPersonInfo={handlePersonInfo}
                streaming={pending}
              />
            </div>
          ) : (
            <div
              // Tour snippets: accent border (the transparent base keeps plain
              // bubbles the same size). The step currently on screen gets a soft
              // accent ring + tint ("you are here"). Old threads' stored colors
              // are deliberately ignored.
              className={`rounded-lg border-2 px-3 py-2 text-smycles-body leading-relaxed text-ink ${
                isTourSnippet ? "border-accent-decoration/50" : "border-transparent"
              } ${
                isCurrentStep
                  ? "bg-accent/10 smycles-selected ring-[3px] ring-accent-decoration"
                  : "bg-ink/[0.06]"
              } ${
                clickable
                  ? "cursor-pointer transition hover:ring-1 hover:ring-ink/20"
                  : ""
              }`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => revisitSnippet(msg) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        revisitSnippet(msg);
                      }
                    }
                  : undefined
              }
            >
              {/* What produced this answer, on demand: the turn's progress lines, kept when the
                  turn ended. Collapsed by default — the answer is what the reader came for — and
                  absent entirely on reloaded threads, which carry no trail. */}
              {msg.thinking && msg.thinking.length > 0 && (
                <ThinkingDisclosure steps={msg.thinking} />
              )}
              <AgentMarkdown
                content={content}
                onPersonInfo={handlePersonInfo}
                addedIds={addedIds}
                streaming={pending}
              />
            </div>
          )}
          {!isMine && !isInfo && msg.cards?.map((card) => <AgentCardView key={card.id} card={card} />)}
          {msg.error && (
            <p className="mt-0.5 text-smycles-label text-red-600" role="status">
              {msg.error}
            </p>
          )}
        </div>
      </div>
    );
  };

  // Right-edge panel (@adr SPARK-0019-assistant-lives-on-the-right-edge@2). Desktop: a layout-taking flex sibling of the
  // main content column — opening the drawer squeezes the page rather than
  // overlaying it (Windows 11 Copilot docked pattern). Width animates 0 →
  // 384px (768px for the Grow improve answer), capped at half the viewport.
  // Mobile: too narrow to share space; falls back to a fixed
  // full-width overlay sheet that slides in via translate-x. Non-modal in
  // both modes — only ESC and the launcher close the drawer.
  return (
    <div
      id="agent-drawer"
      role="dialog"
      aria-label={intl.formatMessage({
        id: "agent.title",
        defaultMessage: "Assistant",
      })}
      aria-hidden={!open}
      // `inert` (when closed) removes the entire subtree from the tab order
      // and the accessibility tree in one shot — without it `aria-hidden`
      // alone leaves buttons/textarea reachable via Tab into the invisible
      // drawer.
      inert={!open}
      style={{ "--assistant-w": `min(${shownWidth}px, 50vw)` } as CSSProperties}
      className={[
        // Always: vertical column, hairline left divider, true-white surface. z-[46] keeps the
        // drawer above the tour spotlight blur overlay (z-45, index.css).
        "relative z-[46] flex flex-col border-l border-ink/10 bg-page",
        // Desktop (sm+): inline flex child, user-resizable width via CSS var
        // (capped at half the viewport). Suspend the width transition while
        // dragging so the drawer tracks the pointer instead of easing.
        "shrink-0 overflow-hidden",
        dragging ? "" : "transition-[width] duration-200",
        open ? "sm:w-(--assistant-w)" : "sm:w-0",
        // Mobile (max-sm): fixed overlay sheet, slide via translate-x. Uses
        // the named --z-modal token (see UI_CONVENTIONS § Z-index).
        "max-sm:fixed max-sm:inset-y-0 max-sm:right-0 max-sm:z-[var(--z-modal)] max-sm:w-full max-sm:overflow-visible max-sm:shadow-2xl max-sm:transition-transform",
        open
          ? "max-sm:translate-x-0 max-sm:pointer-events-auto"
          : "max-sm:translate-x-full max-sm:pointer-events-none",
      ].join(" ")}
    >
      {/* Resize handle — left edge, desktop only, pointer-only. No ARIA
          role: a real `separator` would have to expose tabIndex +
          aria-valuemin/max/now + arrow-key resizing to be accessible, and
          drawer resize is a power-user nicety, not a primary affordance. So
          we keep it as a visual handle and let keyboard users dismiss with
          Esc. Hidden from assistive tech via aria-hidden. */}
      <div
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        aria-hidden
        className="absolute inset-y-0 left-0 z-10 hidden w-2 -translate-x-1/2 cursor-ew-resize touch-none sm:block hover:bg-ink/[0.06]"
      />
      <div className="flex items-center gap-2 border-b border-ink/10 px-3 py-2">
        {view === "chat" && (
          <button
            type="button"
            onClick={showList}
            aria-label={intl.formatMessage({
              id: "agent.back",
              defaultMessage: "Back to chats",
            })}
            title={intl.formatMessage({
              id: "agent.back",
              defaultMessage: "Back to chats",
            })}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>
        )}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate text-smycles-body font-semibold text-ink">
            <FormattedMessage id="agent.title" defaultMessage="Assistant" />
          </span>
        </div>
        {view === "list" && (
          <button
            id="agent-newchat"
            type="button"
            onClick={newChat}
            aria-label={intl.formatMessage({
              id: "agent.newChat",
              defaultMessage: "New chat",
            })}
            title={intl.formatMessage({
              id: "agent.newChat",
              defaultMessage: "New chat",
            })}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={intl.formatMessage({
            id: "agent.close",
            defaultMessage: "Close",
          })}
          title={intl.formatMessage({
            id: "agent.close",
            defaultMessage: "Close",
          })}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.75}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {view === "list" ? (
        <ChatsList
          intl={intl}
          threads={threads}
          threadsState={threadsState}
          loadingId={loadingId}
          deletingId={deletingId}
          loadError={loadError}
          onOpenThread={handleOpenThread}
          onRequestDelete={setConfirmDeleteId}
        />
      ) : (
        <>
          <div id="agent-chat-body" className="relative flex min-h-0 flex-1 flex-col" {...(canAttachDocuments ? attachDropProps : {})}>
            {canAttachDocuments && attachDragOver && (
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-accent-decoration bg-page/60 px-3 text-center backdrop-blur-sm">
                <span className="font-serif text-smycles-body text-ink">
                  {intl.formatMessage({ id: "agent.attach.drop", defaultMessage: "Drop to attach to this chat" })}
                </span>
              </div>
            )}
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="flex-1 overflow-y-auto px-4 py-4"
            >
              {messages.length === 0 ? (
                <p className="px-1 text-smycles-body text-ink-muted">
                  <FormattedMessage
                    id="agent.empty"
                    defaultMessage="Ask the assistant about people, profiles and more."
                  />
                </p>
              ) : (
                <PersonChoiceContext.Provider value={choosePersonInChat}>
                <div className="space-y-2">
                  {messages.slice(0, contextBreak?.index).map(renderMessage)}
                  {contextBreak && (
                    // Viewport-high tail: the fresh context fills the screen from the top while
                    // the pre-break conversation sits above it, out of view.
                    <div
                      className="space-y-2"
                      style={{ minHeight: contextBreak.minHeight }}
                    >
                      <div className="flex items-center gap-3 py-1">
                        <div aria-hidden="true" className="h-px flex-1 bg-ink/10" />
                        <span className="shrink-0 text-smycles-label text-ink-muted">
                          <FormattedMessage
                            id="agent.contextBreak"
                            defaultMessage="New context window"
                          />
                        </span>
                        <div aria-hidden="true" className="h-px flex-1 bg-ink/10" />
                      </div>
                      {messages.slice(contextBreak.index).map(renderMessage)}
                    </div>
                  )}
                </div>
                </PersonChoiceContext.Provider>
              )}
            </div>
            {/* Jump to latest: appears only when scrolled up; re-pins (smooth) and re-engages autoscroll. */}
            {!atBottom && messages.length > 0 && (
              <button
                type="button"
                onClick={() => scrollToBottom("smooth")}
                aria-label={intl.formatMessage({
                  id: "agent.jumpToLatest",
                  defaultMessage: "Jump to latest",
                })}
                className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-ink text-ink shadow-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-ink-strong"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"
                  />
                </svg>
              </button>
            )}
          </div>

          {tourActive || resumeTour ? (
            // Tour mode: a visible state banner + the controls replace the chips. Without a live
            // run, `resumeTour` (thread-scoped) renders the same controls in the paused look:
            // play/next continue the interrupted tour at its cursor, skip jumps to the next
            // section, stop dismisses the offer. Back needs a live run's history, so it hides.
            <div className="flex flex-col gap-1.5 px-4 pb-1">
              <div
                className="flex items-center gap-2 px-1 text-smycles-label font-medium tracking-[0.18em] text-ink-muted"
                role="status"
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    tourPaused || !tourActive
                      ? "bg-ink/30"
                      : "animate-pulse bg-accent"
                  }`}
                />
                {tourFinished ? (
                  <FormattedMessage
                    id="agent.tour.modeFinished"
                    defaultMessage="Tour finished. Click a snippet or step back to review; stop exits."
                  />
                ) : tourPaused || !tourActive ? (
                  <FormattedMessage
                    id="agent.tour.modePaused"
                    defaultMessage="Tour paused. You can ask questions now."
                  />
                ) : (
                  <FormattedMessage
                    id="agent.tour.modeActive"
                    defaultMessage="Tour mode"
                  />
                )}
              </div>
              {/* Countdown to the next step (Spotify-style): the bar fills over the current step's
                  dwell so the user sees the tour is about to move on. Keyed by step id so it
                  restarts each step; it FREEZES (does not vanish) while paused, and is hidden in
                  review or while the section question is up (nothing is counting down then). */}
              {tourActive &&
                !tourFinished &&
                !tourQuestion &&
                tourCurrentStepId &&
                tourStepDwellMs > 0 && (
                  <div
                    className="mx-1 h-1 overflow-hidden rounded-full bg-ink/10"
                    role="presentation"
                  >
                    <div
                      key={tourCurrentStepId}
                      className="h-full origin-left rounded-full bg-accent"
                      style={{
                        animation: `assistant-tour-countdown ${tourStepDwellMs}ms linear forwards`,
                        // Freeze mid-fill while paused instead of unmounting; resumes in place.
                        animationPlayState: tourPaused ? "paused" : "running",
                      }}
                    />
                  </div>
                )}
              {/* The /tour-all section question: pick a pillar or continue with the general
                  tour; nothing picked → the timeout continues automatically. */}
              {tourQuestion && (
                <div
                  className="mx-1 h-1 overflow-hidden rounded-full bg-ink/10"
                  role="presentation"
                >
                  {/* Countdown to the question's auto-continue (same bar as the step countdown). */}
                  <div
                    className="h-full origin-left rounded-full bg-accent"
                    style={{
                      animation: `assistant-tour-countdown ${TOUR_QUESTION_TIMEOUT_MS}ms linear forwards`,
                    }}
                  />
                </div>
              )}
              {tourQuestion && (
                <div className="flex flex-wrap gap-1.5">
                  {tourQuestion.options.map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => tourQuestion.choose(id)}
                      className="box-flat rounded-lg border-2 border-ink/25 px-3 py-1 text-smycles-meta font-semibold tracking-wide text-ink transition hover:bg-ink/[0.04] hover:text-ink"
                    >
                      {id === "home" ? (
                        <FormattedMessage
                          id="agent.tour.chipHome"
                          defaultMessage="Main page"
                        />
                      ) : (
                        id
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => tourQuestion.choose(null)}
                    className="box-flat rounded-lg px-3 py-1 text-smycles-meta text-ink transition hover:bg-ink/[0.04] hover:text-ink"
                  >
                    {tourQuestion.followUp ? (
                      <FormattedMessage
                        id="agent.tour.questionFinish"
                        defaultMessage="Finish the tour"
                      />
                    ) : (
                      <FormattedMessage
                        id="agent.tour.questionContinue"
                        defaultMessage="Show everything"
                      />
                    )}
                  </button>
                </div>
              )}
              {/* Icon-only controls; the localized label rides as tooltip + aria-label. */}
              <div className="flex gap-1.5">
                {tourActive && (
                  <TourControlButton
                    label={intl.formatMessage({
                      id: "agent.tour.back",
                      defaultMessage: "Back",
                    })}
                    onClick={() => tourRef.current?.back()}
                    icon="M15.75 19.5L8.25 12l7.5-7.5"
                    anchor="tour.back"
                  />
                )}
                {/* Play/Pause and Skip disappear in review mode: the tour has nothing left to
                    run — only Back/Next through the history and Stop remain meaningful. */}
                {!tourFinished && (
                  <TourControlButton
                    label={
                      tourPaused || !tourActive
                        ? intl.formatMessage({
                            id: "agent.tour.continue",
                            defaultMessage: "Continue",
                          })
                        : intl.formatMessage({
                            id: "agent.tour.pause",
                            defaultMessage: "Pause",
                          })
                    }
                    onClick={() => {
                      if (!tourActive) {
                        if (resumeTour) continueTour(resumeTour);
                      } else if (tourPaused) tourRef.current?.resume();
                      else tourRef.current?.pause();
                    }}
                    icon={
                      tourPaused || !tourActive
                        ? "M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
                        : "M15.75 5.25v13.5m-7.5-13.5v13.5"
                    }
                    anchor="tour.pause"
                  />
                )}
                <TourControlButton
                  label={intl.formatMessage({
                    id: "agent.tour.next",
                    defaultMessage: "Next",
                  })}
                  onClick={() => {
                    if (tourActive) tourRef.current?.next();
                    else if (resumeTour) continueTour(resumeTour);
                  }}
                  icon="M8.25 4.5l7.5 7.5-7.5 7.5"
                  anchor="tour.next"
                />
                {!tourFinished &&
                  (tourActive
                    ? tourIsAll
                    : resumeTour?.command === "/tour-all") && (
                    <TourControlButton
                      label={intl.formatMessage({
                        id: "agent.tour.skipSection",
                        defaultMessage: "Skip section",
                      })}
                      onClick={() => {
                        if (tourActive) tourRef.current?.skipSection();
                        else if (resumeTour) continueTour(resumeTour, true);
                      }}
                      icon="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5"
                      anchor="tour.skipSection"
                    />
                  )}
                <TourControlButton
                  label={intl.formatMessage({
                    id: "agent.tour.stop",
                    defaultMessage: "Stop tour",
                  })}
                  onClick={() => {
                    if (tourActive) {
                      deliberateStopRef.current = true;
                      tourRef.current?.abort();
                    } else setResumeTour(null); // dismiss; reloading the session re-offers
                  }}
                  icon="M6 7.5A1.5 1.5 0 0 1 7.5 6h9A1.5 1.5 0 0 1 18 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 16.5v-9Z"
                  muted
                  anchor="tour.stop"
                />
              </div>
            </div>
          ) : (
            !pending &&
            (chips.length > 0 || messages.length === 0) && (
              <div className="flex flex-col gap-1.5 px-4 pb-1">
                {/* Fixed tour entry point in an empty chat — slash commands are invisible otherwise.
                    The home page has no tour of its own, so there the chip runs the app-wide tour. */}
                {messages.length === 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      void handleSend(
                        location.pathname === "/" ? "/tour-all" : "/tour",
                      )
                    }
                    className="box-flat rounded-lg px-3 py-1.5 text-left text-smycles-meta leading-snug text-ink transition hover:bg-ink/[0.04] hover:text-ink"
                  >
                    {location.pathname === "/" ? (
                      <FormattedMessage
                        id="agent.tour.starterChipAll"
                        defaultMessage="Show me around the app"
                      />
                    ) : (
                      <FormattedMessage
                        id="agent.tour.starterChip"
                        defaultMessage="Show me around this page"
                      />
                    )}
                  </button>
                )}
                {chips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => void handleSend(chip)}
                    className="box-flat rounded-lg px-3 py-1.5 text-left text-smycles-meta leading-snug text-ink transition hover:bg-ink/[0.04] hover:text-ink"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )
          )}

          <div className="px-4 py-3">
            <StudioNotebookChip />
            {canAttachDocuments && (
              <ChatAttachments ref={attachRef} threadId={agentThreadId} ensureThreadId={ensureThreadId} open={open} onSuggestions={setSuggestions} />
            )}
            <div className="bg-page box-flat smycles-ai-shadow relative flex items-end gap-2 p-2 transition focus-within:ring-1 focus-within:ring-ink-accent-ui">
              <div
                onPointerDown={startComposerResize}
                aria-hidden="true"
                className="group absolute inset-x-0 -top-1.5 flex h-3 cursor-ns-resize touch-none items-center justify-center"
              >
                <div className="h-1 w-10 rounded-full bg-ink/15 opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              {canAttachDocuments && (
                <Button
                  id="agent-attach"
                  variant="icon"
                  onClick={() => attachRef.current?.openPicker()}
                  disabled={pending}
                  aria-label={intl.formatMessage({
                    id: "agent.attach.button",
                    defaultMessage: "Attach a document",
                  })}
                  className="shrink-0"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
                    />
                  </svg>
                </Button>
              )}
              <WebSearchToggle disabled={pending || tourActive} />
              {companySearchModeShown && (
                <CompanySearchMode section={section} disabled={pending || tourActive} />
              )}
              <textarea
                id="agent-input"
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => {
                  // A drag pin belongs to the current draft: starting a new draft (old value
                  // empty) or clearing the field drops it, so pasting into a fresh composer
                  // auto-grows even if the handle was dragged before.
                  if (input === "" || e.target.value === "") {
                    manualResizeRef.current = false;
                  }
                  setInput(e.target.value);
                  autoGrowInput();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder={intl.formatMessage({
                  id: "agent.placeholder",
                  defaultMessage: "Ask the assistant…",
                })}
                aria-label={intl.formatMessage({
                  id: "agent.input",
                  defaultMessage: "Assistant input",
                })}
                className="min-h-9 max-h-[50dvh] min-w-0 flex-1 resize-none bg-transparent px-2 py-1.5 text-smycles-body leading-relaxed outline-none placeholder:text-ink-muted"
              />
              {/* One control, two jobs: nothing can be sent while a turn runs, so the send button's
                  place is where the user already looks when they want it to stop. */}
              {pending ? (
                <button
                  type="button"
                  onClick={() => void handleStop()}
                  aria-label={intl.formatMessage({
                    id: "agent.stop",
                    defaultMessage: "Stop",
                  })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-ink text-ink transition-opacity hover:opacity-80"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="1.5" />
                  </svg>
                </button>
              ) : (
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={!input.trim() || pending}
                aria-label={intl.formatMessage({
                  id: "agent.send",
                  defaultMessage: "Send",
                })}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-ink text-ink transition-opacity hover:opacity-80 disabled:opacity-20"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"
                  />
                </svg>
              </button>
              )}
            </div>
            {showExternalContentNotice && (
              <p
                id="agent-external-notice"
                role="note"
                className="px-1 pt-1.5 text-smycles-label leading-snug text-ink-muted"
              >
                <FormattedMessage
                  id="agent.externalContentNotice"
                  defaultMessage="Includes content from the web. AI can make mistakes, please check the sources."
                />
              </p>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title={
          <FormattedMessage
            id="agent.deleteTitle"
            defaultMessage="Delete chat?"
          />
        }
        message={
          <FormattedMessage
            id="agent.deleteMessage"
            defaultMessage="This conversation will be permanently deleted."
          />
        }
        confirmLabel={
          <FormattedMessage id="common.delete" defaultMessage="Delete" />
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}

interface ChatsListProps {
  intl: IntlShape;
  threads: AgentThreadRef[];
  threadsState: "idle" | "loading" | "error";
  loadingId: string | null;
  deletingId: string | null;
  loadError: boolean;
  onOpenThread: (id: string) => void;
  onRequestDelete: (id: string) => void;
}

function ChatsList({
  intl,
  threads,
  threadsState,
  loadingId,
  deletingId,
  loadError,
  onOpenThread,
  onRequestDelete,
}: ChatsListProps) {
  const busy = loadingId !== null || deletingId !== null;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {loadError && (
          <p className="px-2 py-1 text-smycles-label text-red-600" role="status">
            <FormattedMessage
              id="agent.chatOpenError"
              defaultMessage="Couldn't load that chat. Try again."
            />
          </p>
        )}

        {threadsState === "loading" && threads.length === 0 ? (
          <p className="px-2 py-6 text-center text-smycles-body text-ink-muted">
            <FormattedMessage
              id="agent.chatsLoading"
              defaultMessage="Loading…"
            />
          </p>
        ) : threadsState === "error" && threads.length === 0 ? (
          <p className="px-2 py-6 text-center text-smycles-body text-ink-muted">
            <FormattedMessage
              id="agent.chatsError"
              defaultMessage="Couldn't load chats."
            />
          </p>
        ) : threads.length === 0 ? (
          <p className="px-2 py-6 text-center text-smycles-body text-ink-muted">
            <FormattedMessage
              id="agent.chatsEmpty"
              defaultMessage="No chats yet. Start a new one above."
            />
          </p>
        ) : (
          <ul className="space-y-0.5">
            {threads.map((t) => (
              <li
                key={t.id}
                className="group flex items-center rounded-md hover:bg-ink/[0.06]"
              >
                <button
                  type="button"
                  onClick={() => onOpenThread(t.id)}
                  disabled={busy}
                  className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left disabled:opacity-50"
                >
                  <span className="min-w-0 flex-1 truncate text-smycles-body text-ink">
                    {t.preview?.trim()
                      ? renderInlineBold(t.preview.trim())
                      : intl.formatMessage({
                          id: "agent.untitled",
                          defaultMessage: "Untitled chat",
                        })}
                  </span>
                  <span className="shrink-0 text-smycles-micro text-ink-muted">
                    {loadingId === t.id ? "…" : relativeTime(intl, t.updatedAt)}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onRequestDelete(t.id)}
                  disabled={busy}
                  aria-label={intl.formatMessage({
                    id: "agent.deleteChat",
                    defaultMessage: "Delete chat",
                  })}
                  title={intl.formatMessage({
                    id: "agent.deleteChat",
                    defaultMessage: "Delete chat",
                  })}
                  className="mr-1 grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted opacity-0 transition hover:bg-ink/[0.06] hover:text-red-600 focus:opacity-100 disabled:opacity-40 group-hover:opacity-100"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.75}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Compact localized "x minutes ago" for a session's updatedAt.
function relativeTime(intl: IntlShape, iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60)
    return intl.formatRelativeTime(diffSec, "second", { numeric: "auto" });
  if (abs < 3600)
    return intl.formatRelativeTime(Math.round(diffSec / 60), "minute", {
      numeric: "auto",
    });
  if (abs < 86400)
    return intl.formatRelativeTime(Math.round(diffSec / 3600), "hour", {
      numeric: "auto",
    });
  return intl.formatRelativeTime(Math.round(diffSec / 86400), "day", {
    numeric: "auto",
  });
}

function InfoGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
      <circle
        cx="8"
        cy="8"
        r="6.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
      <line
        x1="8"
        y1="7.2"
        x2="8"
        y2="11.4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

// One icon-only tour control: the localized label becomes tooltip + accessible name.
function TourControlButton({
  label,
  onClick,
  icon,
  muted = false,
  anchor,
}: {
  label: string;
  onClick: () => void;
  /** SVG path (24×24 viewBox, stroked like the drawer's other glyphs). */
  icon: string;
  muted?: boolean;
  /** data-assistant-anchor so the tour's controls walk can point a hint card at this button. */
  anchor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-assistant-anchor={anchor}
      className={`box-flat flex flex-1 items-center justify-center rounded-lg px-3 py-1.5 transition hover:bg-ink/[0.04] ${
        muted ? "text-ink-muted hover:text-ink" : "text-ink"
      }`}
    >
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.75}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
      </svg>
    </button>
  );
}

// How many steps stay on screen. A two-condition rescue is eight lines — the condition headers, the
// queries under each, and the combine — so a smaller window would hide the path it took.
const PROGRESS_DEPTH = 8;

/**
 * What the turn has done so far, in the pill and the position the bouncing dots used to occupy.
 *
 * The last [PROGRESS_DEPTH] steps, oldest first, the newest carrying the animated dots. Earlier
 * steps dim rather than disappear, so the path reads as a sequence instead of a flicker.
 * `aria-live` because the dots announced nothing at all.
 */

/**
 * The finished answer's thinking, behind a disclosure.
 *
 * The live trail scrolls past and is cleared when the turn ends, so an answer stood alone with no
 * way to see what produced it — that five searches ran, that each was cut at ten of two hundred,
 * that one company was chosen out of nineteen (Max, 2026-09-30). Collapsed by default: the answer
 * is what the reader came for, and the working is what they ask for when it looks wrong.
 */
function ThinkingDisclosure({ steps }: { steps: AgentProgress[] }) {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const label = open
    ? intl.formatMessage({ id: "agent.thinking.hide", defaultMessage: "Hide what led to this answer" })
    : intl.formatMessage({ id: "agent.thinking.show", defaultMessage: "Show what led to this answer" });
  return (
    <>
      {/* ITS OWN LINE above the answer, not a float beside it. A float was meant to keep the
          control out of the text's way, but a flex container establishes its own formatting
          context and so AVOIDS floats rather than flowing under them — which left the first
          company row of an answer visibly narrower than every row below it (2026-10-12). */}
      <div className="-mt-1 mb-0.5 flex justify-end">
        <button
          type="button"
          aria-expanded={open}
          aria-label={label}
          title={label}
          className="-mr-1 rounded p-1 text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
          onClick={(e) => {
            // The bubble itself can be clickable (tour snippets); this control is not that click.
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path
              d={open ? "M2 7.5 L6 3.5 L10 7.5" : "M2 4.5 L6 8.5 L10 4.5"}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {open && (
        <div className="mb-2 flex flex-col gap-0.5 border-b border-ink/10 pb-2 text-smycles-meta leading-relaxed">
          {/* Same grouping as the live trail: one rule, so the finished view matches what was on
              screen. `current` is false throughout — nothing is in flight any more. */}
          {groupTrail(steps).map((item, i) =>
            item.kind === "searches" ? (
              <SearchTable key={i} steps={item.steps} current={false} />
            ) : (
              <ProgressLine key={i} step={item.step} current={false} />
            ),
          )}
        </div>
      )}
    </>
  );
}

/**
 * Consecutive summary searches, folded into one block.
 *
 * Three searches of the same shape as three sentences is the same sentence three times: what differs
 * is the query and two numbers. As a table the differences line up and the repetition disappears
 * (Max, 2026-09-30). Anything that is not a summary search passes through as its own line.
 */
type TrailItem =
  | { kind: "line"; step: AgentProgress; at: number }
  | { kind: "searches"; steps: AgentProgress[]; at: number };

function groupTrail(steps: AgentProgress[]): TrailItem[] {
  const out: TrailItem[] = [];
  steps.forEach((step, at) => {
    const prev = out[out.length - 1];
    if (step.phase === "searching_summaries") {
      if (prev?.kind === "searches") prev.steps.push(step);
      else out.push({ kind: "searches", steps: [step], at });
    } else {
      out.push({ kind: "line", step, at });
    }
  });
  return out;
}

/**
 * The searches as a table: query, how many of the pool came back, how many were then chosen.
 *
 * A grid rather than padded text, so the columns align whatever the queries are — and `tabular-nums`
 * so the numbers align with each other too.
 */
/**
 * What one search returned, as a cell.
 *
 * The pool is what makes the number mean something: "the closest 10 of 200" is a cut, "3 candidates"
 * is all there was. Without a pool (an older event, or a search that returned fewer than the limit)
 * the plain count is the honest cell — "3 of 0" is neither.
 */
function poolCell(step: AgentProgress, intl: IntlShape): string {
  // Absence, not zero: `Number("")` is 0, so a `?? ""` fallback turned a search that has not
  // reported yet into "no candidates" — a claim about the data made before the search returned.
  if (step.params.count === undefined) return "";
  const count = Number(step.params.count);
  if (!Number.isFinite(count)) return "";
  const pool = Number(step.params.candidates ?? "");
  return Number.isFinite(pool) && pool > 0
    ? intl.formatMessage({ id: "agent.progress.ofPool", defaultMessage: "{count} of {candidates}" }, { count, candidates: pool })
    : intl.formatMessage(
        {
          id: "agent.progress.resultCandidates",
          defaultMessage: "{count, plural, =0 {no candidates} one {# candidate} other {# candidates}}",
        },
        { count },
      );
}

function SearchTable({ steps, current }: { steps: AgentProgress[]; current: boolean }) {
  const intl = useIntl();
  // "the closest 10 of 200" on every row is the same sentence five times: the limit is the turn's
  // configuration and the pool is indexed once per investigation, so it is normally identical
  // throughout. Normally, not always — a search can return fewer than the limit, and a second
  // investigation in the same turn re-indexes — so it is hoisted only when every row agrees, and
  // stays a column when they differ (Max, 2026-09-30).
  const poolCells = steps.map((step) => poolCell(step, intl));
  const shared = poolCells.every((c) => c !== "" && c === poolCells[0]) ? poolCells[0] : null;
  return (
    <div style={steps[0]?.depth ? { paddingLeft: steps[0].depth * 12 } : undefined}>
      <div className="text-ink">
        {shared
          ? intl.formatMessage(
              { id: "agent.progress.searchingSummariesTableEach", defaultMessage: "Searching the summaries for – {each} each:" },
              { each: shared },
            )
          : intl.formatMessage({
              id: "agent.progress.searchingSummariesTable",
              defaultMessage: "Searching the summaries for:",
            })}
      </div>
      <div className={`grid ${shared ? "grid-cols-[1fr_auto]" : "grid-cols-[1fr_auto_auto]"} gap-x-3 text-ink-muted`}>
        {steps.map((step, i) => {
          const selected = step.params.selected;
          return (
            <Fragment key={i}>
              <div className="truncate">{step.params.query ?? ""}</div>
              {!shared && <div className="tabular-nums text-right">{poolCells[i]}</div>}
              <div className="tabular-nums text-right">
                {selected !== undefined && Number.isFinite(Number(selected))
                  ? intl.formatMessage(
                      { id: "agent.progress.chosenCell", defaultMessage: "{selected} chosen" },
                      { selected: Number(selected) },
                    )
                  : ""}
              </div>
            </Fragment>
          );
        })}
      </div>
      {current && <ThinkingDots />}
    </div>
  );
}

function ProgressTrace({ steps }: { steps: AgentProgress[] }) {
  const shown = steps.slice(-PROGRESS_DEPTH);

  return (
    <div
      className="flex flex-col gap-0.5 rounded-lg bg-ink/[0.06] px-3 py-2 text-smycles-meta leading-relaxed"
      role="status"
      aria-live="polite"
    >
      {shown.length === 0 ? (
        <ProgressLine step={null} current />
      ) : (
        groupTrail(shown).map((item, i, all) =>
          item.kind === "searches" ? (
            // Steps repeat once dimmed (two counts of different filters read alike), and the list
            // only ever grows, so the position in the full trace is the stable identity.
            <SearchTable key={steps.length - shown.length + item.at} steps={item.steps} current={i === all.length - 1} />
          ) : (
            <ProgressLine
              key={steps.length - shown.length + item.at}
              step={item.step}
              current={i === all.length - 1}
            />
          ),
        )
      )}
    </div>
  );
}

function ProgressLine({ step, current }: { step: AgentProgress | null; current: boolean }) {
  return (
    // Indented by nesting, so the queries of one sub-exploration read as belonging to the condition
    // announced above them.
    <div
      className={current ? "text-ink" : "text-ink-muted"}
      style={step?.depth ? { paddingLeft: step.depth * 12 } : undefined}
    >
      <ProgressText progress={step} />
      <ProgressResult progress={step} />
      {current && <ThinkingDots />}
    </div>
  );
}

/**
 * What the stage returned, appended to the line that announced it.
 *
 * agent-service reports a stage twice — once when it starts, once with `count` — and the store
 * replaces the first line with the second, so this suffix appears in place rather than as a new
 * line. A stage that has not reported a result shows nothing, which is every stage that has no
 * number to give.
 *
 * A phase whose own sentence already states its result gets NO suffix: `summaries_matched` renders
 * "3 companies found by summary" itself, and the suffix on top read "3 companies found by summary –
 * 3 found", or "No company describes itself that way – none found" at zero (!358 review).
 *
 * Hits are worded as CANDIDATES, not findings: a summary search ranks without a threshold and every
 * hit is still read and judged afterwards, so "3 found" there would promise an answer that does not
 * exist yet (@external-adr AGENT-SERVICE-0032-company-subgraph-description-trigger-and-rag). The
 * exact stages — a field probe, a count, a listing — say "found".
 */
const SELF_COUNTING_PHASES = new Set(["summaries_matched"]);

function ProgressResult({ progress }: { progress: AgentProgress | null }) {
  const intl = useIntl();
  const raw = progress?.params?.count;
  if (raw === undefined || SELF_COUNTING_PHASES.has(progress?.phase ?? "")) return null;
  const count = Number(raw);
  if (!Number.isFinite(count)) return null;
  // A summary search has NO score threshold: the server orders by distance and takes k, so a full
  // list is a cut list and its size says nothing about relevance — the tenth hit is simply the tenth
  // closest of the pool. Saying "10 candidates" reads as a finding; "the closest 10 of 200" is what
  // happened (Max, 2026-09-30).
  const k = Number(progress?.params?.k ?? "");
  const pool = Number(progress?.params?.candidates ?? "");
  const cut = Number.isFinite(k) && k > 0 && count >= k && Number.isFinite(pool) && pool > 0;
  // Reported once more when the verdicts exist: how many of THIS search's hits the model chose. A
  // company returned by several searches is credited to the first of them, so these sum to the
  // number chosen — and "none of them chosen" is the line that was missing while one company ranked
  // first in ten searches and the answer said nothing was found.
  const selected = progress?.params?.selected;
  // How many there were to show, when `count` is only how many fit. Absent on an uncut listing, so
  // NaN here means "nothing was dropped" and the plain count stays.
  const matched = Number(progress?.params?.matched ?? "");
  const base =
    progress?.phase === "searching_summaries"
      ? cut
        ? intl.formatMessage(
            {
              id: "agent.progress.resultCandidatesCut",
              defaultMessage: "the closest {count} of {candidates}",
            },
            { count, candidates: pool },
          )
        : intl.formatMessage(
          {
            id: "agent.progress.resultCandidates",
            defaultMessage: "{count, plural, =0 {no candidates} one {# candidate} other {# candidates}}",
          },
          { count },
        )
      : matched > count
        ? // The budget dropped rows: "1 found" over twenty matches reads as a complete answer, and
          // the nineteen that were cut leave no trace on screen (agent-service #114.2).
          intl.formatMessage(
            {
              id: "agent.progress.resultFoundOfMatched",
              defaultMessage: "{count} of {matched} found",
            },
            { count, matched },
          )
        : intl.formatMessage(
            {
              id: "agent.progress.resultFound",
              defaultMessage: "{count, plural, =0 {none found} other {# found}}",
            },
            { count },
          );
  const message =
    selected !== undefined && Number.isFinite(Number(selected))
      ? `${base}, ${intl.formatMessage(
          {
            id: "agent.progress.resultSelected",
            defaultMessage: "{selected, plural, =0 {none of them chosen} one {# chosen} other {# chosen}}",
          },
          { selected: Number(selected) },
        )}`
      : base;
  return <span className="text-ink-muted"> – {message}</span>;
}

/**
 * The trailing "..." on the step in flight. Opacity-only animation, so nothing reflows and the dots
 * hold their width from the first frame.
 */
function ThinkingDots() {
  return (
    // aria-hidden: decoration. A screen reader announcing the label plus three dots is noise.
    <span aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ animation: `assistant-thinking-dot 1.2s ${i * 200}ms infinite` }}>
          .
        </span>
      ))}
    </span>
  );
}
